import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalBadge, type MinimalTone } from '@ovasabi/ui-minimal'
import { naira } from '../../lib/format'
import { MinusIcon, PlusIcon } from '../icons'
import { mediaSlot } from '../../styles/media'

/* ── Money ───────────────────────────────────────────────────────────── */

const MoneyStyle = {
  /**
   * One ladder, taken from the tokens.
   *
   * Money is rendered app-wide — cards, carts, ops tables and rider earnings — so
   * a private size ladder here is why the same figure looked different on
   * different screens. The sizes below are the app's section, body and caption
   * steps. Tabular numerals above keep columns of figures aligned.
   */
  Root: styled.span<{ $tone: 'brand' | 'default' | 'success'; $size: 'sm' | 'md' | 'lg' }>`
    font-family: ${() => minimalVars.typography.bodyFamily ?? '"Instrument Sans", -apple-system, sans-serif'};
    font-variant-numeric: tabular-nums;
    font-weight: ${minimalVars.typography.weightSemibold};
    white-space: nowrap;
    color: ${({ $tone }) =>
      $tone === 'brand'
        ? minimalVars.color.brand
        : $tone === 'success'
          ? minimalVars.color.success
          : minimalVars.color.textPrimary};
    /* The size ladder reads the tokens, not private values. */
    font-size: ${({ $size }) =>
      $size === 'lg'
        ? minimalVars.typography.h2Size
        : $size === 'sm'
          ? minimalVars.typography.captionSize
          : minimalVars.typography.bodySize};
  `,
  Strike: styled.span`
    font-family: ${() => minimalVars.typography.bodyFamily ?? '"Instrument Sans", -apple-system, sans-serif'};
    font-variant-numeric: tabular-nums;
    color: ${minimalVars.color.textTertiary};
    text-decoration: line-through;
    font-size: ${minimalVars.typography.captionSize};
  `,
  Suffix: styled.span`
    margin-left: 2px;
    color: ${minimalVars.color.textTertiary};
    font-weight: ${minimalVars.typography.weightRegular};
  `,
}

export interface MoneyProps {
  amount: number
  tone?: 'brand' | 'default' | 'success'
  size?: 'sm' | 'md' | 'lg'
  suffix?: string
}

export const Money = ({ amount, tone = 'default', size = 'md', suffix }: MoneyProps) => (
  <MoneyStyle.Root $tone={tone} $size={size}>
    {naira(amount)}
    {suffix ? <MoneyStyle.Suffix>{suffix}</MoneyStyle.Suffix> : null}
  </MoneyStyle.Root>
)

export const StrikePrice = ({ amount, suffix }: { amount: number; suffix?: string }) => (
  <MoneyStyle.Strike>
    {naira(amount)}
    {suffix ? ` ${suffix}` : ''}
  </MoneyStyle.Strike>
)

/* ── Stepper ─────────────────────────────────────────────────────────── */

const StepperStyle = {
  Root: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: ${minimalVars.color.brandSoft};
    border-radius: ${minimalVars.radius.pill};
    padding: 2px 4px;
    height: 38px;
    box-sizing: border-box;
    flex-shrink: 0;

    @media (max-width: 599px) {
      height: 34px;
      padding: 1px 3px;
    }
  `,
  Btn: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${minimalVars.color.brand};
    cursor: pointer;
    border-radius: 50%;
    transition: background-color 140ms ease, transform 100ms ease;

    @media (max-width: 599px) {
      width: 26px;
      height: 26px;
    }

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.6);
      transform: scale(1.06);
    }

    &:active:not(:disabled) {
      transform: scale(0.94);
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  `,
  Value: styled.span`
    min-width: 24px;
    text-align: center;
    /* Stays 0.875rem so the phone step down to captionSize keeps its difference. */
    font-size: 0.875rem;
    font-weight: ${minimalVars.typography.weightBold};
    color: ${minimalVars.color.brand};
    font-variant-numeric: tabular-nums;

    /* 599px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 599px) {
      font-size: ${minimalVars.typography.captionSize};
      min-width: 20px;
    }
  `,
}

export interface StepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  className?: string
}

export const Stepper = ({ value, onChange, min = 1, max = 99, disabled = false, className }: StepperProps) => (
  <StepperStyle.Root className={className} style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
    <StepperStyle.Btn
      type="button"
      aria-label="Decrease quantity"
      disabled={disabled || value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
    >
      <MinusIcon size="sm" />
    </StepperStyle.Btn>
    <StepperStyle.Value aria-live="polite">{value}</StepperStyle.Value>
    <StepperStyle.Btn
      type="button"
      aria-label="Increase quantity"
      disabled={disabled || value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
    >
      <PlusIcon size="sm" />
    </StepperStyle.Btn>
  </StepperStyle.Root>
)

/* ── Avatar ──────────────────────────────────────────────────────────── */

const AvatarStyle = {
  Root: styled.div<{ $size: number }>`
    position: relative;
    flex-shrink: 0;
    &:not([data-fluid]) {
      width: ${({ $size }) => `${$size}px`};
      height: ${({ $size }) => `${$size}px`};
    }
    &[data-fluid] {
      width: 100%;
      aspect-ratio: 1 / 1;
    }
  `,
  Tile: styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    background: ${minimalVars.color.bgSurfaceAlt};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  `,
  /* The glyph slot takes either an emoji (centred, scaled to the avatar) or a
     media node. A media node must fill and crop: left to size itself it
     resolves its percentage box against an auto-width inline span and renders
     at the image's intrinsic size, which is how a chef avatar ends up as a
     full-bleed photo spilling out of its tile. */
  Glyph: styled.span<{ $size: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: ${({ $size }) => `${Math.round($size * 0.42)}px`};
    line-height: 1;
    ${mediaSlot}
  `,
  /* metaSize (11px) rather than the previous 7.5px — below the legibility
     floor and off the type scale. The scrim is deepened slightly so the label
     still holds contrast against light photography at the larger size. */
  Caption: styled.span`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${minimalVars.space['2xs']};
    text-align: center;
    background: rgba(0, 0, 0, 0.42);
    color: ${minimalVars.color.bgSurface};
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  // Corner promo badge (e.g. a "hot/trending" flame). Overflows the clipped tile
  // via the unclipped Root, the standard marketplace card affordance.
  Badge: styled.span`
    position: absolute;
    top: -7px;
    left: -7px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    display: grid;
    place-items: center;
    border: 2px solid ${minimalVars.color.bgSurface};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
    z-index: 2;
  `,
}

export interface AvatarProps {
  glyph: ReactNode
  caption?: string
  size?: number
  /** Optional corner badge (e.g. a flame) overlapping the top-left edge. */
  badge?: ReactNode
  /**
   * Fill the parent's width and stay square, instead of locking to `size` px.
   * Use inside a responsive card so the avatar tracks the card, not a constant.
   * `size` still drives the emoji glyph's type size.
   */
  fluid?: boolean
}

export const Avatar = ({ glyph, caption, size = 56, badge, fluid = false }: AvatarProps) => (
  <AvatarStyle.Root $size={size} data-fluid={fluid || undefined}>
    <AvatarStyle.Tile>
      <AvatarStyle.Glyph $size={size}>{glyph}</AvatarStyle.Glyph>
      {caption ? <AvatarStyle.Caption>{caption}</AvatarStyle.Caption> : null}
    </AvatarStyle.Tile>
    {badge ? <AvatarStyle.Badge>{badge}</AvatarStyle.Badge> : null}
  </AvatarStyle.Root>
)

/* ── StatusBadge ─────────────────────────────────────────────────────── */

export type ChowStatus = 'open' | 'busy' | 'closing' | 'closed'

const statusMap: Record<ChowStatus, { tone: MinimalTone; label: string; solid: boolean }> = {
  open: { tone: 'success', label: 'Open', solid: true },
  busy: { tone: 'warning', label: 'Busy', solid: false },
  closing: { tone: 'danger', label: 'Closing soon', solid: false },
  closed: { tone: 'neutral', label: 'Closed', solid: false },
}

export const StatusBadge = ({ status }: { status: ChowStatus }) => {
  const { tone, label, solid } = statusMap[status]
  return (
    <MinimalBadge tone={tone} emphasis={solid ? 'solid' : 'soft'} size="sm">
      {label}
    </MinimalBadge>
  )
}

/* ── StatusDot ───────────────────────────────────────────────────────── */

const StatusDotStyle = {
  Root: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  `,
  Dot: styled.span<{ $tone: MinimalTone }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $tone }) =>
      $tone === 'success'
        ? minimalVars.color.success
        : $tone === 'warning'
          ? minimalVars.color.warning
          : $tone === 'danger'
            ? minimalVars.color.danger
            : $tone === 'info'
              ? minimalVars.color.info
              : minimalVars.color.textTertiary};
  `,
}

/**
 * Lightweight inline status — a coloured dot + label — for dense list rows
 * where the solid `StatusBadge` pill would crowd the header. Same semantics as
 * `StatusBadge`, lighter footprint.
 */
export interface StatusDotProps {
  status: ChowStatus
  showLabel?: boolean
}

export const StatusDot = ({ status, showLabel = true }: StatusDotProps) => {
  const { tone, label } = statusMap[status]
  return (
    <StatusDotStyle.Root data-status={status}>
      <StatusDotStyle.Dot $tone={tone} />
      {showLabel ? label : null}
    </StatusDotStyle.Root>
  )
}
